// The chrome the SDK draws around a banner's content — the dimmed overlay, the
// card the content sits in, and the close button. It is stored in
// `bannerBlocks.cssStyles` and `recommenders.cssStyles`, and these values are
// the copy of the API's `constants/default-popup-css-styling.js` that the forms
// seed a new or unstyled block with. The two must stay in step.
export const DEFAULT_CHROME_CSS_STYLES = {
  overlayColor: 'rgba(0,0,0,0.4)',
  closeButtonColor: '#000',
  closeFontSize: '14',
  closeButtonBorderRadius: '20',
  closeButtonBackgroundColor: '#fff',
  closeButtonLineHeight: '14',
  closeButtonPadding: '4px 5px',
  closeButtonBorder: '',
  closeButtonTopPosition: '-14',
  closeButtonRightPosition: '-14',
  closeButtonSymbol: 'X',
  closeButtonFontWeight: '400',
  customCss: '',
  cardBackgroundColor: '#fefefe',
  cardWidth: 'auto',
  cardHeight: 'auto',
  cardBorderRadius: '0',
  contentVerticalAlign: 'top',
  // Where the card sits, on two axes, plus the gap it keeps from the edge it
  // is pinned to. `auto` is a real value, not an absence: it means exactly
  // what the display type does today — a pop-up centred in the viewport, a
  // flyout along the bottom on the side `displayPosition` names, a bar at the
  // top or bottom `displayPosition` names — which is what keeps a block that
  // never touches these controls rendering as it always has.
  cardPositionVertical: 'auto',
  cardPositionHorizontal: 'auto',
  cardOffsetVertical: 'auto',
  cardOffsetHorizontal: 'auto',
};

// The "no chrome" shape, seeded for `static` and `custom` blocks, which the SDK
// inserts into the page rather than wrapping in a card.
//
// Spelled out rather than derived from the defaults above on purpose: the
// card, alignment and position keys must be *absent* here, not
// present-and-empty. `''` is a valid-looking value that is wrong on both sides
// of the wire — `contentVerticalAlign` is an enum of top|center|bottom in the
// API, so `''` is a 400 on save, and the SDK resolves each chrome key as
// `cssStyles[key] ?? default`, so `''` survives the fallback and emits
// `background-color: ;`, which the browser drops. Absent is how both sides
// spell "unset". A block seeded here and later switched to `popup` keeps
// whatever it was seeded with, so this is a live path, not a corner.
export const EMPTY_CHROME_CSS_STYLES = {
  overlayColor: '',
  closeButtonColor: '',
  closeFontSize: '',
  closeButtonBorderRadius: '',
  closeButtonBackgroundColor: '',
  closeButtonLineHeight: '',
  closeButtonPadding: '',
  closeButtonBorder: '',
  closeButtonTopPosition: '',
  closeButtonRightPosition: '',
  closeButtonSymbol: '',
  closeButtonFontWeight: '',
  customCss: '',
};

// Which field names a form has to route into `cssStyles` instead of setting on
// the block itself. Derived so that adding a key above is enough.
export const CHROME_CSS_STYLE_KEYS = new Set(Object.keys(DEFAULT_CHROME_CSS_STYLES));

// The display modes the SDK wraps in a fixed modal of its own — overlay,
// display position and close button included — instead of inserting the
// banner into the page. Also the modes the SDK draws a chrome card for,
// which is what the two forms use it for: to decide which default shape to
// seed a block with, and to decide whether the advanced-styling card fields
// are actually reachable before letting one of their stale values veto a
// save (`validateBannerBlock`/`isInvalid` below in each form).
export const MODAL_DISPLAY_TYPES = ['popup', 'bar', 'flyout'];

// The values each position axis can take, `auto` aside. In the SDK's own order
// (`AXIS_PLACEMENTS` in render.js), because the picker lays them out as a grid
// and the grid reads top-to-bottom, left-to-right.
export const VERTICAL_PLACEMENTS = ['top', 'center', 'bottom'];
export const HORIZONTAL_PLACEMENTS = ['left', 'center', 'right'];

// Where `auto` actually puts the card. `auto` is a real value, not an absence —
// it means "the display type's own default", and each type has a different one,
// with `displayPosition` feeding two of them. This is the admin's copy of
// render.js's fallbacks (`axisPlacement(..., fallback)` per display type), and
// it exists so the placement picker can show the author where the block sits
// today instead of showing them an empty control and leaving them to find out
// by saving.
//
// Keep in step with render.js: a popup is centred on both axes and
// `displayPosition` has never had a say in it; a flyout is on the bottom edge,
// on the side `displayPosition` names and otherwise the right; a bar is on the
// edge `displayPosition` names and otherwise the top, and has no horizontal
// axis at all.
const autoPlacement = (displayType, displayPosition) => {
  if (displayType === 'flyout') {
    return { vertical: 'bottom', horizontal: displayPosition === 'left' ? 'left' : 'right' };
  }

  if (displayType === 'bar') {
    return { vertical: displayPosition === 'bottom' ? 'bottom' : 'top', horizontal: null };
  }

  return { vertical: 'center', horizontal: 'center' };
};

// Where the card actually sits: the author's own axis where they set one, and
// what `auto` resolves to where they did not. A value outside the axis's own
// vocabulary — `auto`, a leftover from the other axis, anything unexpected —
// resolves to the default, exactly as `axisPlacement` does in the SDK, so the
// picker and the shop cannot disagree about a stale value.
export const resolvedCardPlacement = ({ displayType, displayPosition, cssStyles = {} }) => {
  const fallback = autoPlacement(displayType, displayPosition);
  const vertical = cssStyles?.cardPositionVertical;
  const horizontal = cssStyles?.cardPositionHorizontal;

  return {
    vertical: VERTICAL_PLACEMENTS.includes(vertical) ? vertical : fallback.vertical,
    horizontal:
      fallback.horizontal === null
        ? null
        : HORIZONTAL_PLACEMENTS.includes(horizontal)
          ? horizontal
          : fallback.horizontal,
  };
};

// Whether the card is still placed by the display type rather than by the
// author: every axis the type has is `auto`. The picker says so in words,
// because "centred because that is what a pop-up does" and "centred because
// someone chose centre" are the same picture and not the same setting — the
// first follows `displayPosition`, the second overrides it.
export const isAutoCardPlacement = ({ displayType, cssStyles = {} }) => {
  const axes = displayType === 'bar' ? ['cardPositionVertical'] : ['cardPositionVertical', 'cardPositionHorizontal'];

  return axes.every((key) => {
    const value = cssStyles?.[key];
    return !value || value === 'auto';
  });
};

// `cardWidth`/`cardHeight` are free text, but the API accepts only `auto` or a
// number with a `px` or `%` unit — `vw`, `rem` and `calc()` are a 400, and the
// mobile SDKs that adopt these keys next cannot evaluate them either.
const CARD_SIZE_PATTERN = /^(?:auto|\d+(?:\.\d+)?(?:px|%))$/;

export const CARD_SIZE_ERROR = 'Use auto, a pixel value such as 480px, or a percentage such as 70%.';

// An empty field is "unset", which the API fills from its own defaults, so only
// a value the author actually typed can be wrong.
export const cardSizeError = (value) => (!value || CARD_SIZE_PATTERN.test(value) ? null : CARD_SIZE_ERROR);

// The offsets are a gap from an edge rather than a dimension, so the API takes
// a wider set for them than for the sizes above: a bare `0`, which needs no
// unit to mean "flush with the edge", and a negative length, which pulls the
// card back past that edge. `vw`, `rem` and `calc()` are still a 400.
const CARD_OFFSET_PATTERN = /^(?:auto|0|-?\d+(?:\.\d+)?(?:px|%))$/;

export const CARD_OFFSET_ERROR = 'Use auto, 0, or a distance from the edge such as 24px, -20px or 5%.';

export const cardOffsetError = (value) => (!value || CARD_OFFSET_PATTERN.test(value) ? null : CARD_OFFSET_ERROR);

// Whether `PageBlockAdvancedStyling` puts *any* card control on screen for
// this block/recommender: the display type has to be one of the three the SDK
// draws a card for. `cardWidth` narrows this further (see below): a `bar` is
// always full width, so its row is hidden even while this is true.
//
// This used to also require `advancedStyling`, the flag behind the switch that
// revealed the panel. The switch is gone — the Appearance tab *is* the panel —
// so for a modal display type the controls are always on screen, and the flag
// no longer says anything about what the author can see or fix.
export const cardControlsOffered = (displayType) => MODAL_DISPLAY_TYPES.includes(displayType);

// The keys whose FORMAT has no such property for a given display type — not
// merely a control the panel currently has off screen. `bar` is the only
// entry: a bar spans the viewport, so it has no width of its own, no
// horizontal axis to place a card on and no horizontal edge to offset it
// from. These are stripped unconditionally below, regardless of whether
// Advanced styling happens to be on.
//
// `overlayColor` is deliberately NOT here for `flyout`, even though the panel
// hides that row for a flyout too (`{isPopup && …}`): on mobile a flyout is a
// modal route and `sdk-flutter` colours its barrier from this key, so the
// value is still meaningful to the format, just not editable from this panel
// today. That makes it "hidden", the same bucket as a stale `cardWidth`
// behind a toggle the author switched off — not "inapplicable". Stripping it
// would also delete a value the author set while the block *was* a pop-up on
// every `popup → flyout → popup` round trip, with nothing to restore it.
//
// The API's applicability table (`constants/banner-chrome-css-styles.md`)
// marks width/horizontal-position/horizontal-offset as `—` for `bar` the same
// way; this is that part of the table expressed as code.
export const INAPPLICABLE_CSS_STYLE_KEYS = {
  popup: [],
  flyout: [],
  bar: ['overlayColor', 'cardWidth', 'cardPositionHorizontal', 'cardOffsetHorizontal'],
};

// Drops card keys out of `cssStyles`. Two different cases, with two different
// rules, because "hidden" and "meaningless" are not the same thing.
//
// The first, unconditional: `INAPPLICABLE_CSS_STYLE_KEYS[displayType]` — the
// format has no such property, independent of whether Advanced styling
// happens to be on. The only way one becomes meaningful again is a change of
// display type, which is the author re-deciding anyway, so even a *valid*
// value goes.
//
// Keeping them meant the admin and the SDKs disagreed about what a bar is. A
// block authored as a pop-up and switched to a bar carried its `cardWidth`
// and `cardPositionHorizontal` into `cssStyles` behind a hidden control, and
// the SDKs honour what they are sent rather than re-deriving which keys
// apply: measured on device 2026-10-03, a bar carrying `cardWidth: '240px'`
// rendered as a 240dp card, and sdk-flutter drew it 689dp tall. No author
// could produce that through this panel, and now no save can either —
// nothing in the stated SDK contract (`cssStyles[key] ?? default`) has the
// SDKs consult it before honouring `cardWidth`. Measured the same week: 0 of 79 production bars set
// any of the three, so nothing in the field is carrying one on purpose.
//
// Deleted rather than reset to a default: absent is how both sides of the
// wire spell "unset" (see `EMPTY_CHROME_CSS_STYLES` above), the API's
// resolver fills its own default for a missing key, and every SDK reads
// `cssStyles[key] ?? default`. A newly seeded bar therefore stops carrying
// these at all, which renders identically to carrying `auto`.
//
// The second: whatever is left once the display type is no longer a modal
// one. The control is off screen but the
// value still describes something applicable to the format, so only a value
// that would actually 400 goes: `validateBannerBlock`/`isInvalid` already
// stop gating Save on a value the author can no longer see or edit, which
// re-enables Save but would otherwise leave a stale, API-rejectable value in
// the saved `cssStyles` verbatim, and the API 400s on it with no field left
// to point the author at. A *valid* size sitting behind a hidden control is
// not that problem — it is stored data the author deliberately set, and a
// chrome key this function doesn't otherwise touch survives a save
// untouched, so a valid card size has to survive it too. Gating the delete
// on the key's own validator — `cardSizeError` for the sizes, `cardOffsetError`
// for the offsets, which the API judges by a wider rule — keeps both ends
// closed: no 400 (the only value that can produce one is exactly the one
// this removes), no silent loss of a value the API would have accepted.
export const pruneUnreachableCardCssStyles = (cssStyles, { displayType }) => {
  if (!cssStyles) {
    return cssStyles;
  }

  const pruned = { ...cssStyles };

  for (const key of INAPPLICABLE_CSS_STYLE_KEYS[displayType] ?? []) {
    delete pruned[key];
  }

  if (!cardControlsOffered(displayType)) {
    if (cardSizeError(pruned.cardWidth)) {
      delete pruned.cardWidth;
    }
    if (cardSizeError(pruned.cardHeight)) {
      delete pruned.cardHeight;
    }
    if (cardOffsetError(pruned.cardOffsetVertical)) {
      delete pruned.cardOffsetVertical;
    }
    if (cardOffsetError(pruned.cardOffsetHorizontal)) {
      delete pruned.cardOffsetHorizontal;
    }
  }

  return pruned;
};
